import { debounce } from '../../filters/lib/debounce'

const TABLE_SETTINGS_SAVE_DELAY = 500
const TABLE_SETTINGS_DEBOUNCE_OPTIONS = { leading: false, trailing: true }

export interface TableSettingsSaveController<TPayload> {
  cancel: () => void
  flush: () => void
  queue: (_payload: TPayload) => void
  saveImmediately: (_payload: TPayload) => void
  setSaveHandler: (_handler: TableSettingsSaveHandler<TPayload>) => void
}

export interface TableSettingsSaveHandler<TPayload> {
  onError: (_payload: TPayload, _error: unknown) => void
  onSuccess: (_payload: TPayload) => void
  save: (_payload: TPayload) => Promise<void>
}

/**
 * Keeps one debounced timer alive for the lifetime of a table settings hook while delegating to
 * the most recent render's save handler.
 */
export const createTableSettingsSaveController = <
  TPayload,
>(): TableSettingsSaveController<TPayload> => {
  let handler: null | TableSettingsSaveHandler<TPayload> = null
  let active: null | { handler: TableSettingsSaveHandler<TPayload>; payload: TPayload } = null
  let pending: null | { handler: null | TableSettingsSaveHandler<TPayload>; payload: TPayload } =
    null
  let pendingIsReady = false

  const startNext = () => {
    if (active || pending === null || !pendingIsReady) return

    const nextHandler = pending.handler ?? handler
    if (!nextHandler) return

    const next = { handler: nextHandler, payload: pending.payload }
    active = next
    pending = null
    pendingIsReady = false

    void next.handler.save(next.payload).then(
      () => settle(next),
      error => settle(next, error),
    )
  }

  const settle = (
    completed: { handler: TableSettingsSaveHandler<TPayload>; payload: TPayload },
    error?: unknown,
  ) => {
    if (active !== completed) return
    active = null

    if (pending !== null) {
      startNext()
      return
    }

    if (error === undefined) {
      completed.handler.onSuccess(completed.payload)
    } else {
      completed.handler.onError(completed.payload, error)
    }
  }

  const debouncedStart = debounce(
    () => {
      pendingIsReady = true
      startNext()
    },
    TABLE_SETTINGS_SAVE_DELAY,
    TABLE_SETTINGS_DEBOUNCE_OPTIONS,
  )

  return {
    cancel: () => {
      debouncedStart.cancel()
      pending = null
      pendingIsReady = false
    },
    flush: () => {
      debouncedStart.cancel()
      if (pending === null) return
      pending.handler ??= handler
      pendingIsReady = true
      startNext()
    },
    queue: payload => {
      pending = { handler: null, payload }
      pendingIsReady = false
      debouncedStart()
    },
    saveImmediately: payload => {
      debouncedStart.cancel()
      pending = { handler, payload }
      pendingIsReady = true
      startNext()
    },
    setSaveHandler: nextHandler => {
      handler = nextHandler
      startNext()
    },
  }
}
